import { useState } from 'react';

export interface ServiceImage {
  id: string;
  src: string;
  alt: string;
  description?: string;
}

interface ServiceGalleryProps {
  images: ServiceImage[];
  title: string;
  layout?: 'grid' | 'masonry';
  className?: string;
}

const ServiceGallery = ({ 
  images, 
  title, 
  layout = 'grid',
  className = ''
}: ServiceGalleryProps) => {
  const [selectedImage, setSelectedImage] = useState<ServiceImage | null>(null);

  const openModal = (image: ServiceImage) => {
    setSelectedImage(image);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'unset';
  };

  return (
    <section className={`py-16 bg-black ${className}`}>
      <div className="container mx-auto px-4">
        <h2 className="text-4xl font-bold text-white text-center mb-12">{title}</h2>
        
        {/* Dynamic Layout */}
        <div className={layout === 'masonry' 
          ? "columns-1 md:columns-2 lg:columns-3 gap-4 space-y-4"
          : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        }>
          {images.map((image) => (
            <div 
              key={image.id}
              className={`${layout === 'masonry' ? 'break-inside-avoid' : ''} 
                cursor-pointer transform transition-transform hover:scale-105`}
              onClick={() => openModal(image)}
            >
              <div className="relative rounded-lg overflow-hidden">
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-auto"
                />
                {image.description && (
                  <div className="absolute top-2 left-2 max-w-[80%] bg-black bg-opacity-60 backdrop-blur-sm rounded p-2 shadow-md">
                    <p className="text-white text-xs font-medium leading-tight">
                      {image.description}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Modal */}
        {selectedImage && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <div 
              className="relative max-w-4xl w-full"
              onClick={e => e.stopPropagation()}
            >
              <button
                className="absolute top-4 right-4 text-white text-xl hover:text-gray-300"
                onClick={closeModal}
              >
                ✕
              </button>
              <img
                src={selectedImage.src}
                alt={selectedImage.alt}
                className="w-full h-auto rounded-lg"
              />
              {selectedImage.description && (
                <div className="mt-4 p-4 bg-black bg-opacity-70 rounded-lg">
                  <p className="text-white text-center">{selectedImage.description}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ServiceGallery; 